import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link, withRouter } from 'react-router-dom';
import { SectionProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Checkbox from '../elements/Checkbox';
import FormHint from '../elements/FormHint';
import Button from '../elements/Button';
import { login, register } from '../../api/auth';
import { AuthContext } from '../../context/AuthContext';
import { getPasswordRules } from '../../utils/passwordValidation';
import {
  getTermsAcceptancePayload,
  PRIVACY_POLICY_PATH,
  TERMS_AND_CONDITIONS_PATH
} from '../../constants/legal';

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

const AuthForm = ({
  initialMode = 'login',
  history,
  location,
  ...props
}) => {
  const [mode, setMode] = useState(initialMode);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);
  const { token, setAuth } = useContext(AuthContext);

  const postLoginPath = location && location.state && location.state.from ? location.state.from : '/account';

  useEffect(() => {
    setMode(initialMode);
    setFieldErrors({});
    setSubmitError('');
  }, [initialMode]);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (token) {
      history.replace(postLoginPath);
    }
  }, [history, postLoginPath, token]);

  const passwordRules = useMemo(() => (
    getPasswordRules(formData.password, formData.confirmPassword)
  ), [formData.confirmPassword, formData.password]);

  const updateField = (e) => {
    const { name, type, value, checked } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));

    setFieldErrors((prevState) => ({
      ...prevState,
      [name]: ''
    }));
    setSubmitError('');
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setFieldErrors({});
    setSubmitError('');
    history.replace(nextMode === 'login' ? '/login' : '/signup');
  };

  const validate = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required.';
    }

    if (!formData.password) {
      errors.password = 'Password is required.';
    }

    if (mode === 'register') {
      const failedRule = passwordRules.find((rule) => !rule.passed);

      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password.';
      }

      if (failedRule) {
        errors.password = 'Please meet all password requirements before continuing.';
      }

      // Validate explicit Terms acceptance before allowing account creation.
      if (!formData.acceptedTerms) {
        errors.acceptedTerms = 'You must agree to the Terms & Conditions before creating an account.';
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.acceptedTerms) {
        setSubmitError(errors.acceptedTerms);
      } else if (mode === 'register' && (errors.password || errors.confirmPassword)) {
        setSubmitError('Please review the highlighted password requirements.');
      } else {
        setSubmitError('');
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = mode === 'login'
        ? await login(formData.email.trim(), formData.password)
        : await register({
            email: formData.email.trim(),
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            password: formData.password,
            // Store the accepted Terms version with registration data via a reusable legal helper.
            ...getTermsAcceptancePayload()
          });

      setAuth(payload);
      history.push(postLoginPath);
    } catch (error) {
      if (isMountedRef.current) {
        setSubmitError(error.message || 'Request failed');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const {
    className,
    topOuterDivider,
    bottomOuterDivider,
    topDivider,
    bottomDivider,
    hasBgColor,
    invertColor
  } = props;

  const outerClasses = classNames(
    'signin section',
    topOuterDivider && 'has-top-divider',
    bottomOuterDivider && 'has-bottom-divider',
    hasBgColor && 'has-bg-color',
    invertColor && 'invert-color',
    className
  );

  const innerClasses = classNames(
    'signin-inner section-inner',
    topDivider && 'has-top-divider',
    bottomDivider && 'has-bottom-divider'
  );

  const sectionHeader = mode === 'login'
    ? {
        title: 'Login or create your Exanta account',
        paragraph: 'Access your Exanta account and start asking your WooCommerce store questions.'
      }
    : {
        title: 'Create your Exanta account',
        paragraph: 'Sign up in minutes and start getting answers from your WooCommerce data.'
      };

  return (
    <section className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          <SectionHeader tag="h1" data={sectionHeader} className="center-content" />
          <div className="tiles-wrap">
            <div className="tiles-item">
              <div className="tiles-item-inner">
                <div className="signin-footer mb-32">
                  <Button
                    color={mode === 'login' ? 'primary' : 'dark'}
                    type="button"
                    onClick={() => switchMode('login')}
                  >
                    Login
                  </Button>
                  <Button
                    color={mode === 'register' ? 'primary' : 'dark'}
                    type="button"
                    onClick={() => switchMode('register')}
                  >
                    Register
                  </Button>
                </div>
                <form onSubmit={handleSubmit} noValidate>
                  <fieldset>
                    {mode === 'register' && (
                      <React.Fragment>
                        <div className="mb-12">
                          <Input
                            id="first_name"
                            name="first_name"
                            label="First name"
                            placeholder="First name"
                            labelHidden
                            value={formData.first_name}
                            onChange={updateField}
                          />
                        </div>
                        <div className="mb-12">
                          <Input
                            id="last_name"
                            name="last_name"
                            label="Last name"
                            placeholder="Last name"
                            labelHidden
                            value={formData.last_name}
                            onChange={updateField}
                          />
                        </div>
                      </React.Fragment>
                    )}

                    <div className="mb-12">
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        label="Email"
                        placeholder="Email"
                        labelHidden
                        value={formData.email}
                        onChange={updateField}
                        required
                        hint={fieldErrors.email}
                        status={fieldErrors.email ? 'error' : ''}
                      />
                    </div>

                    <div className="mb-12">
                      <Input
                        id="password"
                        type="password"
                        name="password"
                        label="Password"
                        placeholder="Password"
                        labelHidden
                        value={formData.password}
                        onChange={updateField}
                        required
                        hint={fieldErrors.password}
                        status={fieldErrors.password ? 'error' : ''}
                      />
                    </div>

                    {mode === 'register' && (
                      <React.Fragment>
                        <div className="mb-12">
                          <Input
                            id="confirmPassword"
                            type="password"
                            name="confirmPassword"
                            label="Confirm password"
                            placeholder="Confirm password"
                            labelHidden
                            value={formData.confirmPassword}
                            onChange={updateField}
                            required
                            hint={fieldErrors.confirmPassword}
                            status={fieldErrors.confirmPassword ? 'error' : ''}
                          />
                        </div>
                        <div className="mb-24">
                          <ul className="list-reset text-sm">
                            {passwordRules.map((rule) => (
                              <li key={rule.key} className={classNames('mb-8', rule.passed ? 'text-color-success' : 'text-color-error')}>
                                <span className="mr-8">{rule.passed ? '✓' : '×'}</span>
                                {rule.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="mb-24 signin-legal-copy">
                          <Checkbox
                            id="acceptedTerms"
                            name="acceptedTerms"
                            checked={formData.acceptedTerms}
                            onChange={updateField}
                            required
                          >
                            <span>
                              I agree to the{' '}
                              <Link
                                to={TERMS_AND_CONDITIONS_PATH}
                                className="func-link"
                                onClick={(event) => event.stopPropagation()}
                              >
                                Terms &amp; Conditions
                              </Link>
                            </span>
                          </Checkbox>
                          {fieldErrors.acceptedTerms && (
                            <FormHint status="error">{fieldErrors.acceptedTerms}</FormHint>
                          )}
                          <p className="mt-12 mb-0 text-xs text-color-low">
                            By creating an account, you acknowledge that you have read our{' '}
                            <Link to={PRIVACY_POLICY_PATH} className="func-link">
                              Privacy Policy
                            </Link>.
                          </p>
                        </div>
                      </React.Fragment>
                    )}

                    {submitError && (
                      <div className="mb-16 text-sm text-color-error">
                        {submitError}
                      </div>
                    )}

                    <div className="mt-24 mb-32">
                      <Button color="primary" wide type="submit" loading={isSubmitting} disabled={isSubmitting}>
                        {mode === 'login' ? 'Sign in' : 'Create account'}
                      </Button>
                    </div>
                  </fieldset>
                </form>
                <div className="signin-bottom has-top-divider">
                  <div className="pt-32 text-xs center-content text-color-low">
                    {mode === 'login' ? (
                      <React.Fragment>
                        Don&apos;t have an account?{' '}
                        <Link to="/signup" className="func-link" onClick={() => switchMode('register')}>
                          Register
                        </Link>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        Already have an account?{' '}
                        <Link to="/login" className="func-link" onClick={() => switchMode('login')}>
                          Login
                        </Link>
                      </React.Fragment>
                    )}
                  </div>
                  {mode === 'login' && (
                    <div className="pt-12 text-xs center-content text-color-low">
                      <Link to="/forgotpassword" className="func-link">
                        Forgot your password?
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

AuthForm.propTypes = propTypes;
AuthForm.defaultProps = defaultProps;

export default withRouter(AuthForm);
